import { shaderMaterial } from '@react-three/drei'
import { extend } from '@react-three/fiber'
import * as THREE from 'three'

const CylinderMaterialShader = shaderMaterial(
  
  {
    uTime: 0,
    uSpeed: 0.5,

    uNoise1Scale: [500.0, 6.0],
    uNoise2Scale: [100.0, 3.0],
    uVoronoiScale: [100.0, 2.1],

    uBandScale: 10.0,
    uBandsNoiseScale: [16.0, 1.3],

    uColorNoiseScale: [100.0, 4.5],
    uColorNoiseStrength: 0.22,

    uEdgeWidth: 0.001,
    uEdgeStrength: 0.8,

    uBaseColor: new THREE.Color('#0d253c'),
    uCyanColor: new THREE.Color('#4e6ecf'),
    uMagentaColor: new THREE.Color('#ff83c4'),
    uEdgeBlueColor: new THREE.Color('#4e6ecf').multiplyScalar(2.0),  // HDR
    uEdgeVioletColor: new THREE.Color('#5b58fd').multiplyScalar(2.0),  // HDR

    uScrollBoost: 0.0,   // animé par le scroll
    uBoostAmount: 2.0,   // à tweaker pour la violence du boost

    fogColor: new THREE.Color(0x000000),
    fogNear: 1.0,
    fogFar: 100.0,

  },
   /* vertex */ `
    #include <common>
    #include <fog_pars_vertex>

    varying vec2 vUv;

    void main() {
      vUv = uv;

      vec3 transformed = position;
      vec4 mvPosition = modelViewMatrix * vec4(transformed, 1.0);
      gl_Position = projectionMatrix * mvPosition;

      #include <fog_vertex>
    }
  `,
  /* fragment */
  `
    #include <fog_pars_fragment>

    uniform float uTime;
    uniform float uSpeed;
    uniform vec2 uNoise1Scale;
    uniform vec2 uNoise2Scale;
    uniform vec2 uVoronoiScale;
    uniform float uBandScale;
    uniform vec2 uBandsNoiseScale;
    uniform float uBandsNoiseStrength;
    uniform vec2 uColorNoiseScale;
    uniform float uColorNoiseStrength;
    uniform float uEdgeWidth;
    uniform float uEdgeStrength;

    uniform vec3 uBaseColor;
    uniform vec3 uCyanColor;
    uniform vec3 uMagentaColor;
    uniform vec3 uEdgeBlueColor;
    uniform vec3 uEdgeVioletColor;

    uniform float uScrollBoost;   // 0 à 1
    uniform float uBoostAmount; 
    

    varying vec2 vUv;

    float hash(vec2 p) {
      return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
    }

    float noise(vec2 p) {
      vec2 i = floor(p);
      vec2 f = fract(p);

      float a = hash(i);
      float b = hash(i + vec2(1.0, 0.0));
      float c = hash(i + vec2(0.0, 1.0));
      float d = hash(i + vec2(1.0, 1.0));

      vec2 u = f * f * (3.0 - 2.0 * f);
      return mix(a, b, u.x) +
        (c - a) * u.y * (1.0 - u.x) +
        (d - b) * u.x * u.y;
    }

    float voronoi(vec2 x) {
      vec2 n = floor(x);
      vec2 f = fract(x);
      float res = 1.0;

      for (int j = -1; j <= 1; j++) {
        for (int i = -1; i <= 1; i++) {
          vec2 g = vec2(float(i), float(j));
          float h = hash(n + g);
           vec2 o = vec2(h, h) * 0.8;
          vec2 r = g + o - f;
          res = min(res, dot(r, r));
        }
      }
      return sqrt(res);
    }

    float fbm(vec2 p) {
      float value = 0.0;
      float amplitude = 0.5;
      for(int i = 0; i < 4; i++) {
        value += noise(p) * amplitude;
        p *= 2.0;
        amplitude *= 0.5;
      }
      return value;
    }

    void main() {
      vec2 uv = vUv;

      // défilement de bas vers haut
      uv.y += uTime;

      // --------- BOOST DOUX DES NOISES ---------
      // uScrollBoost vient de React (0 → 1)
      float easedBoost = pow(uScrollBoost, 2.0);

      // intensités relatives
      float boost1   = 1.0 + easedBoost * (uBoostAmount * 0.0);          // fort sur noise1
      float boost2   = 1.0 + easedBoost * (uBoostAmount * 0.0);  // moyen sur noise2
      float boostVor = 1.0 + easedBoost * (uBoostAmount * 2.0);  // léger sur voronoi Y

      vec2 noise1Scale = vec2(uNoise1Scale.x * boost1, uNoise1Scale.y);
      vec2 noise2Scale = vec2(uNoise2Scale.x * boost2, uNoise2Scale.y);
      vec2 voronoiScale = vec2(uVoronoiScale.x , uVoronoiScale.y * boostVor);
      // -----------------------------------------

      // --- Noise principal ---
      float n1 = noise(vec2(
        uv.x * noise1Scale.x,
        uv.y * noise1Scale.y
      ));

      float n2 = noise(vec2(
        uv.x * noise2Scale.x,
        uv.y * noise2Scale.y
      ) + 10.0);

      float v = 1.0 - voronoi(vec2(
        uv.x * voronoiScale.x,
        uv.y * voronoiScale.y
      ));

      float pattern = mix(n1, v, 0.6);
      pattern += n2 * 0.25;
      pattern = clamp(pattern * 1.5, 0.0, 1.0);

      // --- Streaks directionnels par FBM (scale contrôlable) ---

      vec2 uvStreak = vec2(
        uv.x * uBandsNoiseScale.x,
        uv.y * uBandsNoiseScale.y + uTime * 0.2
      );

      float streak = fbm(uvStreak + uTime);

      // masque streaks → transition douce
      streak = smoothstep(0.3, 0.8, streak);

      // fusion dans le pattern global
      pattern *= streak ;

      // pour la couleur
      float bands = streak;


      vec3 base            = uBaseColor;
      vec3 cyan            = uCyanColor;
      vec3 magenta         = uMagentaColor;
      vec3 edgeColorBlue   = uEdgeBlueColor;
      vec3 edgeColorViolet = uEdgeVioletColor;

      // --- noise de couleur sur cyan/magenta ---
      float colorN = noise(vec2(
        uv.x * uColorNoiseScale.x,
        uv.y * uColorNoiseScale.y + uTime * 0.1
      ));

      float nCentered = (colorN - 0.5) * 2.0;

      vec3 noisyCyan = clamp(cyan + nCentered * uColorNoiseStrength, 0.0, 1.0);
      vec3 noisyMagenta = clamp(magenta - nCentered * uColorNoiseStrength, 0.0, 1.0);

      // bande couleur (cyan -> magenta)
      vec3 stripeColor = mix(noisyCyan, noisyMagenta, bands);

      // couleur de base (fond + bandes)
      vec3 color = mix(base, stripeColor, pattern);

      // ---------- CONTOURS AVEC BRUIT n2 COHÉRENT ----------

      // n2 vient du pattern → petite perturbation pour les bords
      float nEdge = (n2 - 0.5) * 0.2;  // 0.2 = intensité de déformation

      // 1) frontière interne : cyan / magenta
      // bands ~ 0 -> cyan, ~1 -> magenta, ~0.5 -> jonction
      float edgeCMZone = abs(bands - 0.5);
      float noisyEdgeCMZone = edgeCMZone + nEdge;
      float edgeCM = 1.0 - smoothstep(uEdgeWidth, uEdgeWidth + 0.15, noisyEdgeCMZone);

      // 2) frontière externe : cyan / base
      float cyanMask = 1.0 - bands;            // 1 dans le cyan, 0 côté magenta
      float edgeBaseZone = abs(pattern - 0.25);
      float noisyEdgeBaseZone = edgeBaseZone + nEdge;
      float edgeBase = 1.0 - smoothstep(uEdgeWidth, uEdgeWidth + 0.15, noisyEdgeBaseZone);
      edgeBase *= cyanMask;

      // 3) nouvelle frontière fine côté magenta, proche de la jonction
      float magMask = bands;                   // 1 dans le magenta, 0 côté cyan
      float violetCenter = 0.58;
      float edgeMagZone = abs(bands - violetCenter);
      float noisyEdgeMagZone = edgeMagZone + nEdge;
      float edgeMag = 1.0 - smoothstep(uEdgeWidth * 1.2, uEdgeWidth + 0.1, noisyEdgeMagZone);
      edgeMag *= magMask;

      // combinaisons
      float edgeBlue = clamp(edgeCM + edgeBase, 0.0, 1.0); // bleu sur jonctions existantes
      float edgeViolet = clamp(edgeMag, 0.0, 1.0);         // violet sur bord magenta

      // edgeColorBlue = clamp(edgeColorBlue, 0.0, 1.0);
      // edgeColorViolet = clamp(edgeColorViolet, 0.0, 1.0);

      // on injecte d'abord le bleu électrique, puis le violet
      color = mix(color, edgeColorBlue, edgeBlue * uEdgeStrength);
      color = mix(color, edgeColorViolet, edgeViolet * uEdgeStrength);

      gl_FragColor = vec4(color, 1.0);

      #include <fog_fragment>

    }
  `,
  (self) => {
    self.fog = true
  }
)

extend({ CylinderMaterialShader })

export { CylinderMaterialShader }
